import { useEffect, useMemo, useState } from 'react';
import { Dropdown, Spinner, Subtitle } from '../../components/shared';
import { useSelector } from 'react-redux';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import moment from 'moment';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
} from 'chart.js';
import { sendSingleRequest } from '../../apis';

ChartJS.register(LinearScale, Title, PointElement, LineElement);

interface IEagleLocation {
  id: string;
  name: string;

  salinityNodes: Array<{
    id: string;
    name: string;
  }>;
}

interface IValue {
  ts: string;
  val: number;
}

const SalinityChart = () => {
  const lang = useSelector(selectLang);

  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<IEagleLocation[]>([]);
  const [curLoc, setCurLoc] = useState<IEagleLocation>();
  const [salinityData, setSalinityData] = useState<IValue[]>([]);

  const loadSalinity = async (location: IEagleLocation) => {
    const response = await sendSingleRequest(
      {
        salinity_ids: location.salinityNodes.map(x => x.id),
        start_time: moment().subtract(2, 'week').format('YYYY-MM-DD'),
      },
      'GET',
      'api/eagle-io/salinity',
      true,
    );
    if (!response.status) {
      window.alert(
        response.data?.message ||
          translate(lang, '_failed_load_salinity_data'),
      );
    }
    setSalinityData(response.data || []);
  };

  const selectLocation = async (locId: string) => {
    setLoading(true);
    const location = locations.find(x => x.id === locId);
    if (location) {
      setCurLoc(location);
      await loadSalinity(location);
    }
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const response = await sendSingleRequest(
        {},
        'GET',
        'api/eagle-io/locations',
        true,
      );
      if (!response.status) {
        window.alert(
          response.data?.message || translate(lang, '_failed_load_locations'),
        );
        return;
      }

      const locs: IEagleLocation[] = response.data;
      setLocations(locs);

      if (locs.length > 0) {
        setCurLoc(locs[0]);
        await loadSalinity(locs[0]);
      }
      setLoading(false);
    })();
  }, []);

  const chartData = useMemo(() => {
    return {
      labels: salinityData.map(x => moment(x.ts).format('MMM DD, HH:mm')),
      datasets: [
        {
          label: translate(lang, 'Salinity'),
          data: salinityData.map(x => x.val),
          borderColor: 'blue',
          backgroundColor: 'rgba(0, 0, 255, 0.1)',
        },
      ],
    };
  }, [salinityData, lang]);

  return (
    <div className='pb-32'>
      <Subtitle size={4} color='black' align='center' fontWeight={600}>
        {translate(lang, '_salinity_data_month')}
      </Subtitle>
      <div className='mb-16 mxw-300'>
        <Dropdown
          label={translate(lang, 'Location')}
          options={locations.map(x => ({
            id: x.id,
            label: x.name,
            value: x.id,
          }))}
          value={curLoc?.id}
          onChange={selectLocation}
        />
      </div>
      {loading ? (
        <div className='mt-32 mb-32'>
          <Spinner />
        </div>
      ) : (
        <div>
          <Line
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
                title: { display: false },
              },
              spanGaps: true,
              scales: {
                x: {
                  ticks: {
                    autoSkip: false,
                    callback: function (val, index, ticks) {
                      if (typeof val !== 'number') return '';

                      const label = this.getLabelForValue(val);
                      const currentDay = label.split(',')[0];

                      const prev = ticks[index - 1];
                      const prevLabel = prev
                        ? this.getLabelForValue(prev.value)
                        : '';
                      const prevDay = prevLabel.split(',')[0];

                      return currentDay !== prevDay ? currentDay : '';
                    },
                    maxRotation: 30,
                  },
                },
              },
            }}
            data={chartData}
          />
        </div>
      )}
    </div>
  );
};

export default SalinityChart;
